/* eslint-disable */
import React from "react";
import styled from "styled-components";
import numeral from "numeral";
import { useTranslation } from "react-i18next";
import { ApexDonutChart, ApexLineChart } from "@xfrontend/cycraft-chart";
import { GetInfo, ArrowRight } from "@xfrontend/xicon";
import ScoreGauge from "./ScoreGauge";
import PercentageBar from "./PercentageBar";
import InfoStat from "./InfoStat";

const StatItem = styled.div`
  display: flex;
  justify-content: center;
  color: var(--x-gray-400);
  gap: 6px;

  span.value {
    color: var(--x-gray-200);
    font-weight: bold;
    font-size: 20px;
  }
`;

const LegendGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  font-size: 14px;
  margin-bottom: 12px;
  width: 100%;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--x-gray-300);
`;

const Dot = styled.div<{ color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${(props: { color: string }) => props.color};
`;

interface BotServiceCardProps {
  data: any;
}

const BotServiceCard: React.FC<BotServiceCardProps> = ({ data }) => {
  const { t } = useTranslation("dashboard");
  const totalStats = data.stats.reduce(
    (acc: number, curr: any) => acc + (curr.value || 0),
    0,
  );

  const threatStats = [
    { value: data.inputUnsafe, label: t("card.stats.policyViolations") },
    { value: data.outputUnsafe, label: t("card.stats.customPolicyViolations") },
  ];

  const requestStats = [
    { value: data.requests.stats.requests, label: t("card.stats.totalRequests") },
    { value: data.requests.stats.apiErrors, label: t("card.stats.sessions") },
    { value: data.requests.stats.mcpCalls, label: t("card.stats.toolCalls") },
  ];

  return (
    <div>
      <div className="d-flex justify-content-between" style={{ gap: "18px" }}>
        <div className="mx-auto" style={{ width: "260px", padding: "8px" }}>
          <div className="title" style={{ marginBottom: "28px" }}>
            {data.title} <ArrowRight size={14} color="var(--x-gray-200)" />
          </div>
          <div
            className="position-relative overflow-hidden mb-4"
            style={{ height: "150px", padding: "25px 32px" }}
          >
            <div
              className="position-absolute top-0 end-0"
              style={{ opacity: "0.5" }}
            >
              <GetInfo size={14} color="var(--x-gray-200)" />
            </div>

            <div className="d-flex justify-content-center">
              <ScoreGauge
                source={data?.score?.value ?? null}
                sourceColor={data?.score?.sourceColor ?? null}
                sourcePieColor={data?.score?.sourcePieColor ?? null}
                target={data?.score?.target ?? null}
                targetPieColor={data?.score?.targetPieColor ?? null}
              />
            </div>
          </div>
          <div className="mx-1">
            <div style={{ color: "var(--x-gray-400)", fontSize: "14px", marginBottom: "8px" }}>
              {t("card.riskLevelBreakdown")}
            </div>
            <div className="mb-4">
              <PercentageBar list={data.stats} total={totalStats} />
            </div>
            <div
              className="d-flex justify-content-between"
              style={{ color: "var(--x-gray-400)" }}
            >
              <LegendGrid>
                {data.stats.map((s: any) => (
                  <LegendItem key={s.label}>
                    <Dot color={s.bg} /> {s.label}{" "}
                    <span style={{ marginLeft: "8px" }}>{s.value}</span>
                  </LegendItem>
                ))}
              </LegendGrid>
            </div>
          </div>
        </div>

        <div className="d-flex flex-column flex-grow-1">
          <div className="d-block d-xl-flex justify-content-between align-items-center mb-3">
            <div
              className="d-flex justify-content-center gap-3"
              style={{ lineHeight: "28px" }}
            >
              <StatItem>
                <span>{t("card.p95Latency")}</span>
                <span className="value">{data.headerStats.p95Latency}</span>
              </StatItem>
              <StatItem>
                <span>{t("card.maxLatency")}</span>
                <span className="value">{data.headerStats.maxLatency}</span>
              </StatItem>
            </div>
          </div>

          <div className="flex-grow-1 d-flex flex-column d-xl-flex flex-xl-row justify-content-between gap-3">
            <div
              className="flex-shrink-1 rounded-2 p-3"
              style={{
                width: "calc(50% - 8px)",
                height: "294px",
                backgroundColor: "var(--basicWrap)",
              }}
            >
              <div className="mb-2">{t("card.securityOverview")}</div>
              <div
                className="d-flex justify-content-between align-items-stretch"
                style={{ height: "calc(100% - 27px)", gap: "12px" }}
              >
                <div className="position-relative flex-grow-1">
                  <div
                    className="position-absolute top-0 end-0"
                    style={{ opacity: "0.5" }}
                  >
                    <GetInfo size={14} color="var(--x-gray-200)" />
                  </div>
                  <div className="position-absolute top-0 bottom-0 start-0 end-0 overflow-hidden">
                    <ApexLineChart
                      height="100%"
                      seriesData={data.threatTrends.series}
                      colors={data.threatTrends.colors}
                      tooltip={{
                        x: {
                          formatter: (_val: number, opts: { dataPointIndex: number }) => (
                            data.threatTrends.tooltipLabels?.[opts.dataPointIndex] ?? ""
                          ),
                        },
                      }}
                      xaxis={{
                        categories: data.threatTrends.categories,
                        tickPlacement: "on",
                        crosshairs: { show: false },
                        tooltip: { enabled: false },
                        axisTicks: { show: false },
                        axisBorder: { enabled: false },
                        labels: {
                          style: {
                            colors: "var(--x-gray-400)",
                            fontFamily: "var(--bs-body-font-family)",
                          },
                        },
                      }}
                      yaxis={[
                        {
                          seriesName: data.threatTrends.series[0].name,
                          min: data.threatTrends.config.amountAxis.min,
                          max: data.threatTrends.config.amountAxis.max,
                          tickAmount:
                            data.threatTrends.config.amountAxis.tickAmount,
                          labels: {
                            style: { colors: "var(--x-gray-400)" },
                            formatter: (val: number) =>
                              typeof val === "number" ? val.toFixed(0) : "",
                          },
                        },
                        {
                          seriesName: [
                            data.threatTrends.series[1].name,
                            data.threatTrends.series[2].name,
                          ],
                          show: false,
                          min: data.threatTrends.config.amountAxis.min,
                          max: data.threatTrends.config.amountAxis.max,
                          tickAmount:
                            data.threatTrends.config.amountAxis.tickAmount,
                        },
                      ]}
                      legend={{
                        markers: {
                          shape: ["line", "circle", "circle"] as any,
                          size: [7, 5, 5],
                          radius: 10,
                          customHTML: undefined,
                          onClick: undefined,
                          strokeWidth: [2, 0, 0],
                          offsetX: -5,
                        },
                        itemMargin: {
                          horizontal: 12,
                        },
                      }}
                      plotOptions={{
                        bar: {
                          columnWidth: "60%",
                        },
                      }}
                      grid={{
                        padding: {
                          right: 30,
                          left: 30,
                        },
                        borderColor: "var(--x-purple-500)",
                        strokeDashArray: 0,
                      }}
                      withLegend
                    />
                  </div>
                </div>
                <div
                  style={{
                    borderRight: "1px solid var(--x-purple-500)",
                    width: "1px",
                    height: "100%",
                  }}
                />
                <div
                  className="d-flex flex-column justify-content-between align-content-stretch gap-3"
                  style={{ width: "25%" }}
                >
                  {threatStats.map((stat, index) => (
                    <React.Fragment key={stat.label}>
                      <InfoStat value={stat.value} label={stat.label} />
                      {index < threatStats.length - 1 && (
                        <div
                          style={{ border: "1px solid var(--x-purple-500)" }}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>

            <div
              className="flex-shrink-1 rounded-2 p-3"
              style={{
                width: "calc(50% - 8px)",
                height: "294px",
                backgroundColor: "var(--basicWrap)",
              }}
            >
              <div className="mb-2">{t("card.applicationActivity")}</div>
              <div
                className="d-flex justify-content-between align-items-stretch"
                style={{ height: "calc(100% - 27px)", gap: "12px" }}
              >
                <div className="position-relative flex-grow-1">
                  <div className="position-absolute top-0 bottom-0 start-0 end-0 overflow-hidden d-flex flex-column justify-content-center align-items-center">
                    <ApexDonutChart
                      title={{
                        text: t("card.policyChecksBreakdown"),
                        offsetX: 100,
                        offsetY: -5,
                        style: {
                          fontSize: "12px",
                          fontWeight: 400,
                          color: "var(--x-gray-400)",
                        },
                      }}
                      wrapperHeight="100%"
                      height="100%"
                      width="115%"
                      seriesData={data.requests.series || []}
                      colors={data.requests.colors}
                      labels={data.requests.labels || []}
                      chart={{
                        offsetY: 30,
                        animations: {
                          speed: 700,
                          dynamicAnimation: {
                            enabled: true,
                            speed: 700,
                          },
                        },
                      }}
                      plotOptions={{
                        expandOnClick: false,
                        pie: {
                          donut: {
                            size: "80%",
                            labels: {
                              show: true,
                              name: {
                                show: false,
                                fontSize: "12px",
                                fontWeight: 500,
                                color: "var(--x-gray-400)",
                              },
                              value: {
                                show: true,
                                fontSize: "20px",
                                fontWeight: 700,
                                color: "var(--x-gray-200)",
                                formatter: (val) => {
                                  const total = data.requests?.series?.reduce(
                                    (a, b) => a + b,
                                    0,
                                  );

                                  return `${total !== 0 ? numeral(parseFloat((val / total) * 100)).format("0,0.[00]") : 0}%`;
                                },
                              },
                              total: {
                                show: false,
                                label: "Total",
                                fontSize: "12px",
                                fontWeight: 500,
                                color: "var(--x-gray-400)",
                              },
                            },
                          },
                        },
                      }}
                      legend={{
                        show: true,
                        showForSingleSeries: false,
                        showForNullSeries: true,
                        showForZeroSeries: true,
                        position: "right",
                        horizontalAlign: "center",
                        floating: false,
                        fontSize: "12px",
                        fontFamily: "Helvetica, Arial",
                        fontWeight: 400,
                        formatter: undefined,
                        inverseOrder: false,
                        width: 155,
                        height: undefined,
                        tooltipHoverFormatter: undefined,
                        customLegendItems: [],
                        offsetX: -15,
                        offsetY: -5,
                        labels: {
                          useSeriesColors: false,
                        },
                        markers: {
                          size: 5,
                          strokeWidth: 0,
                          strokeColor: "#fff",
                          fillColors: data.requests.colors || [],
                          radius: 10,
                          customHTML: undefined,
                          onClick: undefined,
                          offsetX: -5,
                          offsetY: 0,
                        },
                      }}
                    />
                  </div>
                </div>
                <div
                  style={{
                    borderRight: "1px solid var(--x-purple-500)",
                    width: "1px",
                    height: "100%",
                  }}
                />
                <div
                  className="d-flex flex-column justify-content-between align-content-stretch gap-3"
                  style={{ width: "25%" }}
                >
                  {requestStats.map((stat, index) => (
                    <React.Fragment key={stat.label}>
                      <InfoStat value={stat.value} label={stat.label} />
                      {index < requestStats.length - 1 && (
                        <div
                          style={{ border: "1px solid var(--x-purple-500)" }}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BotServiceCard;
